<template>
  <div class="help-button-margin">
    <b-row>
      <b-col cols="2">
        <DiscardSaveButtons
          @save="save"
          @cancel="cancel"
        />
      </b-col>
      <b-col cols="4">
        <FilterSearch
          v-model="search"
          @clear="search = ''"
        />
      </b-col>
      <b-col cols="3">
        <FilterRowsPerPage
          v-model="perPage"
          :max="rows"
          @getAll="perPage = rows"
        />
      </b-col>
      <b-col cols="3">
        <b-pagination
          v-model="currentPage"
          align="fill"
          :total-rows="rows"
          :per-page="perPage"
          aria-controls="csv-table"
        />
      </b-col>
    </b-row>
    <b-table
      id="csv-table"
      responsive
      bordered
      small
      :per-page="perPage"
      :current-page="currentPage"
      :fields="headers"
      :items="csvdatacopy"
      :busy="!appLoaded"
      :filter="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-compare="sortCompare"
      @filtered="filtered"
    >
      <template slot="table-busy">
        <div class="text-center text-primary">
          <b-spinner class="align-middle" />
          <strong> Loading...</strong>
        </div>
      </template>

      <template
        v-for="(header,index) in headers"
        v-slot:[`cell(${header.key})`]="data"
      >
        <b-input
          :key="index"
          v-model="data.item[header.key].value"
          size="sm"
          :disabled="disabledfields.includes(header.key)"
          :type="type(data.item[header.key].value)"
          :state="state(data.item[header.key].value)"
          @change="valueChanged(data.item, header.key, $event)"
        />
      </template>

      <template v-slot:cell(recordid)="data">
        <b-input
          v-model="data.value"
          size="sm"
          disabled
          :type="type(data.value)"
          :state="state(data.value)"
        />
      </template>
    </b-table>
  </div>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import DiscardSaveButtons from '@/components/admin/editData/DiscardSaveButtons.vue'
import FilterRowsPerPage from '@/components/admin/editData/FilterRowsPerPage.vue'
import FilterSearch from '@/components/common/FilterSearch.vue'

import { updateData, UpdateDataPayloadItem } from '@/api'
import {
  RecordResponse,
  RecordItem,
} from '@/types'

const RECORD_ID = 'recordid'

const cloneDeep = require('lodash.clonedeep')

const components = {
  DiscardSaveButtons,
  FilterRowsPerPage,
  FilterSearch,
}

// eslint-disable-next-line no-use-before-define
@Component<EditDataView>({
  components,
  watch: {
    csvdata: {
      handler () {
        this.csvdatacopy = cloneDeep(this.csvdata)
      },
      deep: true,
    },
  },
})
export default class EditDataView extends Vue {
  sortBy = RECORD_ID
  sortDesc = false
  perPage = 20
  currentPage = 1
  search = ''
  csvdatacopy: RecordResponse[] = []
  filteredRowsLength!: number

  async mounted () {
    this.$typedStore.primary.app.setAppLoaded(false)
    this.$typedStore.admin.csvData.resetChangedCSVData()
    await Promise.all([
      this.$typedStore.admin.csvData.getCSVData(),
      this.$typedStore.visualisationData.getTreeData(),
      this.$typedStore.primary.app.getConfig(),
    ])
    this.$typedStore.primary.app.setAppLoaded(true)
  }

  beforeDestroy () {
    this.cancel()
  }

  get levels () {
    return this.$typedStore.primary.app.levels.levels
  }

  get geomColumn () {
    return this.$typedStore.primary.app.config.appConfig.geomColumn || ''
  }

  get disabledfields () {
    return [RECORD_ID, ...this.levels.map(f => f.column), this.geomColumn]
  }

  get editablefields () {
    return Object.keys(this.csvdatacopy[0]).filter(key => {
      return !this.disabledfields.includes(key)
    }).sort()
  }

  get csvdata () {
    return this.$typedStore.admin.csvData.csvdata
  }

  get headers () {
    let results: { key: string, label: string, sortable: boolean }[] = []
    if (this.csvdatacopy.length > 0) {
      results = this.disabledfields.concat(this.editablefields)
        .map((key) => {
          return {
            key: key,
            label: key,
            sortable: true,
          }
        })
    }
    return results
  }

  get changedcsvdata () {
    return this.$typedStore.admin.csvData.changedcsvdata
  }

  get appLoaded () {
    return this.$typedStore.primary.app.appLoaded
  }

  get rows () {
    return this.filteredRowsLength ?? this.csvdatacopy.length
  }

  async save () {
    this.$typedStore.primary.app.setAppLoaded(false)
    if (this.changedcsvdata.length > 0) await updateData(this.changedcsvdata)
    await Promise.all([
      this.$typedStore.admin.csvData.getCSVData(),
      this.$typedStore.visualisationData.getTreeData(),
    ])
    this.$typedStore.admin.csvData.resetChangedCSVData()
    this.$typedStore.primary.app.setAppLoaded(true)
  }

  cancel () {
    this.csvdatacopy = cloneDeep(this.csvdata)
    this.$typedStore.admin.csvData.resetChangedCSVData()
  }

  valueChanged (dataset: RecordResponse, datakey: keyof RecordResponse, value: unknown) {
    if (value) {
      const payload = {
        recordid: dataset.recordid,
        dataid: (dataset[datakey] as RecordItem).dataid,
        key: datakey,
        value: value,
      } as UpdateDataPayloadItem
      this.$typedStore.admin.csvData.addChangedCSVData(payload)
    }
  }

  isNumber (value: any) {
    return typeof value === 'number' || !isNaN(Number(value))
  }

  isRecordItem (value: any): value is RecordItem {
    return 'dataid' in value && 'value' in value
  }

  state (value: unknown) {
    return value ? null : false
  }

  type (value: unknown) {
    if (this.isNumber(value)) return 'number'
    return 'text'
  }

  checkCustomValues (a: number | string, b: number | string) {
    // If they are objects, check their custom values
    if (this.isNumber(a) && this.isNumber(b)) {
      return Number(a) < Number(b) ? -1 : Number(a) > Number(b) ? 1 : 0
    } else {
      return this.toString(a as string).localeCompare(this.toString(b as string), undefined, {
        numeric: true,
      })
    }
  }

  sortCompare (a: RecordResponse, b: RecordResponse, key: keyof RecordResponse) {
    if (this.isNumber(a[key]) && this.isNumber(b[key])) {
      // If both compared fields are native numbers
      return Number(a[key]) < Number(b[key]) ? -1 : Number(a[key]) > Number(b[key]) ? 1 : 0
    } else if (this.isRecordItem(a[key]) && this.isRecordItem(b[key])) {
      return this.checkCustomValues((a[key] as RecordItem).value, (b[key] as RecordItem).value)
    } else {
      // Stringify the field data and use String.localeCompare
      return this.toString(a[key]).localeCompare(this.toString(b[key]), undefined, {
        numeric: true,
      })
    }
  }

  toString (value: unknown): string {
    if (!value) {
      return ''
    } else if (value) {
      const object = value as Record<string, any>
      return Object.keys(object)
        .sort()
        .map(key => this.toString(object[key]))
        .join(' ')
    }
    return String(value)
  }

  filtered (_: unknown, length: number) {
    this.filteredRowsLength = length
  }
}

</script>

<style>

#csv-table td {
  padding: 0;
}

</style>

<style scoped>

.help-button-margin {
  margin-bottom: 36px;
}

</style>
